/**
 * TanStack Query hooks for actor-facing project endpoints.
 *
 * The API surface here matches PRD §6 (project / character_assignment / batch
 * read paths). Mutations live separately in their respective files (cues for
 * navigation state, takes for upload, comments for frame.io). This module is
 * read-only.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

/** Frame-rate descriptor matching @ohear/shared timecode FrameRate. */
export interface ApiFrameRate {
  fps: number
  drop_frame: boolean
}

/** Per-actor project summary returned by GET /api/v1/actor/projects. */
export interface ActorProject {
  project_id: string
  project_code: string
  episode: number
  title: string
  client_name: string
  duration_sec: number
  frame_rate: ApiFrameRate
  /** Server-generated HLS playlist for the full episode (story-mode tab). */
  story_mode_hls_url: string | null
  /**
   * Character assignments for this actor on this project. An actor may hold
   * multiple roles on the same project (e.g. lead + named townsperson), so
   * this is always an array. Most surfaces only render `characters[0]`; the
   * Story Mode header eventually shows a dropdown if length > 1.
   */
  characters: Array<{
    character_id: string
    character_code: string
    display_name: string
  }>
  /** Total cues for the actor across all batches. */
  total_cues: number
  /** Cues approved (counts toward progress). */
  approved_cues: number
  /** Active batch number (1-based); null if no batch is in progress. */
  current_batch: number | null
  total_batches: number
  /** Per-batch summary, ordered by batch_number ascending. */
  batches: ActorBatchSummary[]
}

export interface ActorBatchSummary {
  batch_id: string
  batch_number: number
  total_cues: number
  approved_cues: number
  pickup_pending_cues: number
  status: 'not_started' | 'in_progress' | 'awaiting_review' | 'approved'
  /** Wall-clock estimate of remaining work, seconds. Null if unknown. */
  remaining_sec: number | null
}

const projectKey = (projectId: string) => ['actor', 'project', projectId] as const

/**
 * Fetch the full per-project actor view: character, batch list, story-mode
 * HLS URL. Refetches on reconnect because actors often pop offline mid-take.
 */
export function useActorProject(projectId: string) {
  return useQuery({
    queryKey: projectKey(projectId),
    queryFn: () => api<ActorProject>(`/api/v1/actor/projects/${projectId}`),
    enabled: Boolean(projectId),
  })
}

const projectsListKey = ['actor', 'projects'] as const

/** A row in the actor projects list. Aggregated across every org the actor
 *  has membership in; `organization` identifies which org each row came from
 *  so the FE can badge cards (and `is_active` flags the currently-selected
 *  org from the OrgSwitcher). */
export interface ActorProjectListItem
  extends Pick<ActorProject, 'project_id' | 'project_code' | 'episode' | 'title' | 'client_name'> {
  organization: {
    id: string
    name: string
    is_active: boolean
  }
}

export interface ActorProjectsList {
  projects: ActorProjectListItem[]
}

/** List of projects the actor is assigned to (for the project switcher). */
export function useActorProjects() {
  return useQuery({
    queryKey: projectsListKey,
    queryFn: () => api<ActorProjectsList>('/api/v1/actor/projects'),
  })
}
